import type { CreditTransaction } from '@localagent/sdk';

const CREDIT_COSTS: Record<string, number> = {
  agent_invocation: 3,
  agent_tool_call: 1,
};

const METRIC_LABELS: Record<string, string> = {
  agent_invocation: 'Agent Invocations',
  agent_tool_call: 'Tool Calls',
};

interface MetricRow {
  metric: string;
  label: string;
  events: number;
  credits: number;
}

export function UsageBreakdown({ transactions }: { transactions: CreditTransaction[] }) {
  const consumptionTxs = transactions.filter((t) => t.transaction_type === 'consumption');

  const byMetric = new Map<string, number>();
  for (const tx of consumptionTxs) {
    const metric = (tx.metadata?.metric_key as string) ?? 'other';
    byMetric.set(metric, (byMetric.get(metric) ?? 0) + Math.abs(tx.amount));
  }

  const rows: MetricRow[] = [];
  let totalCredits = 0;

  for (const [metric, credits] of byMetric.entries()) {
    const costPerEvent = CREDIT_COSTS[metric] ?? 1;
    const events = Math.round(credits / costPerEvent);
    rows.push({
      metric,
      label: METRIC_LABELS[metric] ?? metric,
      events,
      credits,
    });
    totalCredits += credits;
  }

  rows.sort((a, b) => b.credits - a.credits);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg bg-card p-4">
        <h3 className="text-label font-medium text-muted-foreground">Usage Breakdown</h3>
        <p className="mt-2 text-body text-muted-foreground">No usage recorded this period.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-card p-4 space-y-3">
      <h3 className="text-label font-medium text-muted-foreground">Usage Breakdown</h3>
      <table className="w-full text-body">
        <thead>
          <tr className="border-b text-caption text-muted-foreground">
            <th className="py-1 text-left font-medium">Metric</th>
            <th className="py-1 text-right font-medium">Events</th>
            <th className="py-1 text-right font-medium">Credits</th>
            <th className="py-1 text-right font-medium">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.metric} className="border-b last:border-0">
              <td className="py-1.5">{row.label}</td>
              <td className="py-1.5 text-right text-muted-foreground">{row.events.toLocaleString()}</td>
              <td className="py-1.5 text-right">{row.credits.toLocaleString()}</td>
              <td className="py-1.5 text-right text-muted-foreground">
                {totalCredits > 0 ? Math.round((row.credits / totalCredits) * 100) : 0}%
              </td>
            </tr>
          ))}
          <tr className="font-medium">
            <td className="py-1.5">Total</td>
            <td className="py-1.5 text-right" />
            <td className="py-1.5 text-right">{totalCredits.toLocaleString()}</td>
            <td className="py-1.5 text-right">100%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
